import { useState, useEffect, useMemo, useRef } from "react";
import { useFiltersStore } from "@/stores/filtersStore";
import { useGlobalData } from "@/context/globalContext";
import { loadItems } from "@/lib/client/api";

export type ResumeSearchState = {
  showResume: boolean;
  unreadCount: number;
  resumeFrom: string | null;
  lastViewedAt: string | null;
  newUnreadCount: number;
  showNew: boolean;
};

/**
 * Detects when a user has items to catch up on for their active saved filter.
 *
 * Returns `showResume: true` when there are items older than `resume_from` (unfinished scrolling).
 * Returns `showNew: true` when there are items newer than `resume_from` (new since last session).
 *
 * Both require:
 *  - A saved filter is selected
 *  - The filter has a `resume_from` timestamp set (user has scrolled before)
 *  - `last_checked_app` is more recent than `resume_from` (user opened filter in a new session)
 */
export function useResumeSearch(): ResumeSearchState {
  const [unreadCount, setUnreadCount] = useState(0);
  const [newUnreadCount, setNewUnreadCount] = useState(0);

  const savedFilterId = useFiltersStore((s) => s.savedFilterId);
  const savedFilters = useFiltersStore((s) => s.savedFilters);
  const activeFilters = useFiltersStore((s) => s.activeFilters);
  const searchText = useFiltersStore((s) => s.searchText);
  const { exchangeRates } = useGlobalData();

  const activeFiltersKey = useMemo(
    () => JSON.stringify({ activeFilters, searchText }),
    [activeFilters, searchText]
  );
  const activeFiltersRef = useRef(activeFilters);
  const searchTextRef = useRef(searchText);
  const exchangeRatesRef = useRef(exchangeRates);
  const lastCountKeyRef = useRef<string | null>(null);
  activeFiltersRef.current = activeFilters;
  searchTextRef.current = searchText;
  exchangeRatesRef.current = exchangeRates;

  const selectedFilter = savedFilterId ? savedFilters.find((f) => f.id === savedFilterId) : null;
  const selectedFilterExists = Boolean(selectedFilter);

  const resumeFrom = selectedFilter?.resume_from ?? null;
  const lastViewedAt = selectedFilter?.last_checked_app ?? null;

  useEffect(() => {
    if (!savedFilterId || !selectedFilterExists || !resumeFrom) {
      setUnreadCount(0);
      setNewUnreadCount(0);
      lastCountKeyRef.current = null;
      return;
    }

    const resumeDate = new Date(resumeFrom);
    const lastChecked = lastViewedAt ? new Date(lastViewedAt) : null;

    // No gap — nothing new to catch up on
    if (!lastChecked || lastChecked <= resumeDate) {
      setUnreadCount(0);
      setNewUnreadCount(0);
      return;
    }

    // Pagination advances resume_from and replaces the saved-filter object.
    // That is not a new session and should not repeat the resume count.
    const countKey = `${savedFilterId}:${lastViewedAt}`;
    if (lastCountKeyRef.current === countKey) {
      return;
    }
    lastCountKeyRef.current = countKey;

    const controller = new AbortController();
    const query = activeFiltersRef.current as Parameters<typeof loadItems>[0]["query"];

    // Count items older than resume_from (unfinished scrolling)
    loadItems({
      query,
      searchText: searchTextRef.current,
      limit: 1,
      offset: 0,
      exchangeRates: exchangeRatesRef.current,
      filterid: savedFilterId,
      countOnly: true,
      resumeFrom,
      signal: controller.signal,
    })
      .then((result) => {
        if (!controller.signal.aborted) {
          setUnreadCount(result?.total ?? 0);
        }
      })
      .catch(() => {});

    // The new-results UI is intentionally disabled, so do not issue a second
    // count request for data that is not rendered.
    setNewUnreadCount(0);

    return () => controller.abort();
  }, [savedFilterId, resumeFrom, selectedFilterExists, lastViewedAt, activeFiltersKey]);

  return {
    showResume: unreadCount > 0,
    unreadCount,
    resumeFrom,
    lastViewedAt,
    newUnreadCount,
    showNew: newUnreadCount > 0,
  };
}
